import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Label, Switch } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import { convertLongTimeToTotalMinutes } from "../../../../app/datetimeUtils";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import { LessonUsageRow, useLazyLessonUsageQuery } from "./adminUsageReportApiSlice";
import { LessonUsageTable } from "./LessonUsageTable";
import { LessonUsageChart } from "./LessonUsageChart";

export default function LessonUsage() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<LessonUsageRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [hideZeroRows, setHideZeroRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callLessonUsage] = useLazyLessonUsageQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callLessonUsage(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	const enrichedRows = useMemo(() => {
		if (!data) return [];
		return data.rows.map((row) => ({
			...row,
			totalMinutes: convertLongTimeToTotalMinutes(row.timeSpent),
		}));
	}, [data]);

	const filteredRows = useMemo(() => {
		if (!enrichedRows.length) return [];
		if (!hideZeroRows) return enrichedRows;
		return enrichedRows.filter((row) => row.totalMinutes > 0);
	}, [enrichedRows, hideZeroRows]);

	return (
		<section>
			<h1>
				{reportLevel} Lesson Usage by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">{`The Lesson Usage Report shows the amount of time spent on lessons for each ${reportSubLevelName(reportLevel).toLowerCase()}. Use the filters below ${reportLevel !== ReportLevel.School ? "or select the name in the table" : ""} to view data for that selection or hide rows with no time spent in lessons. Click the three dots to print or download.`}</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="flex flex-wrap gap-3 items-end print:hidden">
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
								<Field className="w-full md:w-auto flex-none">
									<Label
										as="span"
										className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
										Hide {reportSubLevelName(reportLevel)}s with No Time Spent
									</Label>
									<Switch
										checked={hideZeroRows}
										onChange={setHideZeroRows}
										name="hideZeroRows"
										id="hideZeroRows"
										className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
										/>
									</Switch>
								</Field>
							</div>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={filteredRows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="lesson-usage"
								downloadExcelLink={`${aspUrl}/AdminReports/LessonUsageExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/LessonUsageCsv?${queryString}`}
							/>
						</div>

						<LessonUsageTable
							className="mt-4"
							level={reportLevel}
							fullData={data}
							filteredRows={filteredRows}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<LessonUsageChart
								level={reportLevel}
								data={filteredRows}
								hasData={filteredRows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
